import {
  BankOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  LoginOutlined,
  PlusOutlined,
  TagsOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import EventSponsorLevels from '../components/EventSponsorLevels';
import EventTicketTypes from '../components/EventTicketTypes';
import ImageUpload from '../components/ImageUpload';
import { LogoThumb } from '../components/LogoThumb';
import api, { FRONTEND_URL } from '../config/api';
import { fetchEventCurrency } from '../config/eventCurrency';
import { useAuthStore } from '../store/authStore';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Option } = Select;

interface Event {
  id: string;
  title: string;
  slug: string;
  description?: string;
  startDate: string;
  endDate: string;
  location?: string;
  venue?: string;
  mapLatitude?: number;
  mapLongitude?: number;
  mapZoom?: number;
  coverImage?: string;
  logo?: string;
  isActive: boolean;
  isPublic: boolean;
  accessMode?: 'OPEN' | 'TICKET_REQUIRED' | 'PRIVATE';
  language?: 'HU' | 'EN' | 'SR' | 'NL';
  maxAttendees?: number;
  timezone?: string;
  organizationId: string;
  Organization?: {
    name: string;
  };
  _count?: {
    Ticket: number;
    Session: number;
    Speaker: number;
  };
}

const Events: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  /**
   * Betöltötte-e a modál a pénznemet a VÉDETT configból (#111).
   *
   * A `GET /events` válasza `@Public()`, és a #74 óta a `config`-ja szűkített:
   * `pricing` nincs benne. Ez az űrlap viszont ÍR, tehát nem tölthette fel
   * magát abból — a `cfg?.pricing?.currency || 'EUR'` minden nem-EUR eseményt
   * EUR-ra írt az első mentésnél. Ha a védett olvasás nem sikerül, a mezőt NEM
   * küldjük el: inkább ne írjunk pénznemet, mint helyőrzőt írjunk.
   */
  const [currencyLoaded, setCurrencyLoaded] = useState(false);
  /**
   * MELYIK eseményre van nyitva a modál — a késve érkező pénznem-válasz őre
   * (#111).
   *
   * A `handleEdit` `async`, a védett `GET /events/:id/config` válasza pedig
   * tetszőleges késéssel jön. Enélkül az őr nélkül a sorrend
   * „A szerkesztése → B szerkesztése → A válasza megérkezik" azt jelenti,
   * hogy A pénzneme B ŰRLAPJÁBA íródik, a mező engedélyezetté válik, és a
   * mentés B-re olyan pénznemet ír, amit a felhasználó nem látott és nem
   * választott — pontosan az a hibacsalád, amit a #111 javít, csak a másik
   * irányból. Mérve: `PATCH /events/evt-b` `currency: 'RSD'`.
   *
   * Azért `useRef` és nem állapot: az őrt az `await` UTÁN, a lezárt
   * változóból kell olvasni, és a `useState` értéke ott a régi renderé
   * maradna.
   */
  const editingIdRef = useRef<string | null>(null);

  /**
   * A modál minden zárása egy helyen, hogy az őr ne tudjon elavulni: a
   * `setModalVisible(false)` három hívási helye közül egy kihagyott is elég
   * lenne ahhoz, hogy a ref egy már nem szerkesztett eseményt nevezzen.
   */
  const closeModal = () => {
    editingIdRef.current = null;
    setModalVisible(false);
  };
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [ticketTypesModalVisible, setTicketTypesModalVisible] = useState(false);
  const [sponsorLevelsModalVisible, setSponsorLevelsModalVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [form] = Form.useForm();
  const { user } = useAuthStore();
  const { t } = useTranslation();

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (user) {
      fetchOrganizations();
    }
  }, [user]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await api.get('/events');
      setEvents(response.data.data || []);
    } catch (error) {
      message.error(t('events.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      // SUPER_ADMIN látja az összes szervezetet — akkor is, ha történetesen
      // van organizationId-je. Egyébként csak a saját org-ja jelenik meg.
      if (user?.role === 'SUPER_ADMIN') {
        const response = await api.get('/organizations');
        setOrganizations(response.data || []);
      } else if (user?.organizationId) {
        setOrganizations([
          {
            id: user.organizationId,
            name: user.organization?.name || t('events.ownOrganizationFallback'),
          },
        ]);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const handleCreate = () => {
    // Új esemény: nincs szerkesztett azonosító, tehát egy még úton lévő
    // szerkesztő-válasz ide sem írhat be.
    editingIdRef.current = null;
    setEditingEvent(null);
    form.resetFields();
    // Set default values
    form.setFieldsValue({
      organizationId: user?.organizationId || organizations[0]?.id,
      isActive: true,
      isPublic: true,
      pwaEnabled: true,
      accessMode: 'TICKET_REQUIRED',
      timezone: 'Europe/Budapest',
      language: 'HU',
      currency: 'EUR',
      allowedRegistrantTypes: ['company', 'individual'],
    });
    setModalVisible(true);
  };

  const handleEdit = async (record: Event) => {
    editingIdRef.current = record.id;
    setEditingEvent(record);
    setCurrencyLoaded(false);
    // A mező az előző modál pénznemét (vagy az `initialValue="EUR"`-t) mutatná
    // addig, amíg a védett olvasás meg nem jön — és ha az elhasal, végig. Egy
    // MÁSIK esemény pénzneme szürkén kiírva félrevezet, ezért inkább üres, és
    // a helyőrző látszik.
    form.setFieldValue('currency', undefined);
    const cfg = (record as any).config ?? {};
    form.setFieldsValue({
      ...record,
      slug: record.slug || '',
      dates: [dayjs(record.startDate), dayjs(record.endDate)],
      timezone: record.timezone || 'Europe/Budapest',
      coverImage: record.coverImage || '',
      logo: record.logo || '',
      mapLatitude: record.mapLatitude,
      mapLongitude: record.mapLongitude,
      mapZoom: record.mapZoom || 15,
      allowedRegistrantTypes: cfg?.registration?.allowedRegistrantTypes || [
        'company',
        'individual',
      ],
    });
    setModalVisible(true);

    // A pénznem KÜLÖN, a védett végpontról. `handleEdit` egyetlen eseményre
    // nyílik, tehát ez egy plusz kérés a modál nyitásakor — a listát nem
    // terheli, és nem tágít publikus végpontot.
    // A lekérés a közös segédből jön (#111 utómunka); a VERSENY-ŐR viszont itt
    // marad, mert az erre a modálra jellemző: a segéd nem tudhatja, hogy közben
    // másik eseményre váltottak.
    const currency = await fetchEventCurrency(record.id);
    // Közben másik eseményre (vagy új esemény űrlapjára) válthattak: ilyenkor
    // ez a válasz egy MÁSIK esemény adata, el kell dobni.
    if (editingIdRef.current !== record.id) return;
    if (currency) {
      form.setFieldsValue({ currency });
      setCurrencyLoaded(true);
    }
    // Ha nincs pénznem (elhasalt lekérés is ide fut): marad
    // `currencyLoaded === false`, a mező tiltott lesz, és a mentés nem küldi
    // el. A szervező a Beállítások → Árazás lapon tudja állítani.
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/events/${id}`);
      message.success(t('events.deleted'));
      fetchEvents();
    } catch (error) {
      message.error(t('messages.delete_error'));
    }
  };

  const manageTicketTypes = (event: Event) => {
    setSelectedEvent(event);
    setTicketTypesModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      console.log('Form values:', values);
      const data = {
        ...values,
        startDate: values.dates[0].toISOString(),
        endDate: values.dates[1].toISOString(),
        coverImage: values.coverImage || '',
        logo: values.logo || '',
      };
      delete data.dates;
      console.log('Sending data:', data);

      if (editingEvent) {
        // Csak SUPER_ADMIN tudja szervezetet váltani; egyébként a payload-ból kivesszük.
        if (user?.role !== 'SUPER_ADMIN') {
          delete data.organizationId;
        }
        // Amit nem töltöttünk be, azt nem is írjuk vissza (#111). A backend
        // `buildConfigPatch`-e csak akkor nyúl a `pricing`-hoz, ha kap
        // `currency`-t — hiányában a tárolt érték érintetlen marad.
        if (!currencyLoaded) {
          delete data.currency;
        }
        await api.patch(`/events/${editingEvent.id}`, data);
        message.success(t('events.updated'));
      } else {
        await api.post('/events', data);
        message.success(t('events.created'));
      }

      closeModal();
      fetchEvents();
    } catch (error: any) {
      console.error('Submit error:', error.response?.data);
      const errorData = error.response?.data;
      // A backend AllExceptionsFilter régebbi verziója kétszer csomagolta a
      // ValidationPipe-hibákat (data.message = {message: [...], error, statusCode}).
      // Defenzíven kibontjuk, hogy ha a régi backend még él, akkor is lássuk
      // a valódi üzenetet.
      const raw = errorData?.message;
      const msg = raw && typeof raw === 'object' && 'message' in raw ? (raw as any).message : raw;
      if (Array.isArray(msg)) {
        msg.forEach((m: string) => message.error(m));
      } else {
        message.error(msg || t('events.operationFailed'));
      }
    }
  };

  const columns: ColumnsType<Event> = [
    {
      title: t('events.eventName'),
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <Space>
          {record.logo && (
            <span style={{ marginRight: 8 }}>
              <LogoThumb src={record.logo} alt={record.title} width={80} height={40} />
            </span>
          )}
          <Space direction="vertical" size={0}>
            {/* The name is the row's own link to the event. Until now the only
                way in was the eye icon, which has no label and opens a new tab,
                so the page was effectively undiscoverable. */}
            <Typography.Link
              onClick={() => navigate(`/events/${record.id}`)}
              style={{ fontWeight: 600 }}
            >
              {text}
            </Typography.Link>
            <Text type="secondary" style={{ fontSize: 12 }}>
              /{record.slug}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      title: t('events.dateTime'),
      key: 'date',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span>{dayjs(record.startDate).format('YYYY.MM.DD HH:mm')}</span>
          <span>{dayjs(record.endDate).format('YYYY.MM.DD HH:mm')}</span>
        </Space>
      ),
    },
    {
      title: t('events.location'),
      key: 'location',
      render: (_, record) => (
        <Space>
          <EnvironmentOutlined />
          <span>{record.location || t('events.online')}</span>
        </Space>
      ),
    },
    {
      title: t('events.columnOrganization'),
      key: 'organization',
      render: (_, record) => (
        <Space>
          <BankOutlined />
          <span>{record.Organization?.name || record.organizationId}</span>
        </Space>
      ),
    },
    {
      title: t('events.columnAttendees'),
      key: 'attendees',
      align: 'center',
      render: (_, record) => (
        <Space>
          <TeamOutlined />
          <span>
            {record._count?.Ticket || 0}
            {record.maxAttendees && ` / ${record.maxAttendees}`}
          </span>
        </Space>
      ),
    },
    {
      title: t('events.columnStatus'),
      key: 'status',
      render: (_, record) => {
        const accessLabel: Record<string, { label: string; color: string }> = {
          OPEN: { label: t('events.accessOpenShort'), color: 'green' },
          TICKET_REQUIRED: { label: t('events.accessTicketRequired'), color: 'blue' },
          PRIVATE: { label: t('events.accessPrivateShort'), color: 'red' },
        };
        const mode = record.accessMode ?? (record.isPublic ? 'TICKET_REQUIRED' : 'PRIVATE');
        const a = accessLabel[mode];
        return (
          <Space>
            <Tag color={record.isActive ? 'green' : 'default'}>
              {record.isActive ? t('events.active') : t('events.inactive')}
            </Tag>
            <Tag color={a.color}>{a.label}</Tag>
          </Space>
        );
      },
    },
    {
      title: t('common.actions'),
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<LoginOutlined />}
            onClick={() => window.open(`${FRONTEND_URL}/event/${record.slug}`, '_blank')}
            title={t('events.titlePwaLogin')}
          />
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => window.open(`/events/${record.id}`, '_blank')}
            title={t('events.titleViewDetails')}
          />
          <Button
            type="link"
            icon={<TagsOutlined />}
            onClick={() => manageTicketTypes(record)}
            title={t('events.titleManageTicketTypes')}
          />
          <Button
            type="link"
            icon={<TeamOutlined />}
            onClick={() => {
              setSelectedEvent(record);
              setSponsorLevelsModalVisible(true);
            }}
            title={t('events.titleManageSponsorLevels')}
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title={t('common.edit')}
          />
          <Popconfirm
            title={t('common.confirm_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button type="link" danger icon={<DeleteOutlined />} title={t('common.delete')} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>{t('events.pageTitle')}</Title>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('events.newEvent')}
          </Button>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={events}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => t('events.paginationTotal', { count: total }),
          }}
        />
      </Card>

      <Modal
        title={editingEvent ? t('events.editTitle') : t('events.createTitle')}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label={t('events.eventName')}
                rules={[{ required: true, message: t('common.requiredField') }]}
              >
                <Input placeholder={t('events.namePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="slug"
                label={t('events.slugLabel')}
                rules={[
                  { required: true, message: t('common.requiredField') },
                  {
                    pattern: /^[a-z0-9-]+$/,
                    message: t('events.slugPattern'),
                  },
                ]}
                extra={t('events.slugExtra')}
              >
                <Input
                  placeholder={t('events.slugPlaceholder')}
                  onChange={(e) => {
                    // Automatically format slug as user types
                    const value = e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]/g, '-')
                      .replace(/-+/g, '-')
                      .replace(/^-|-$/g, '');
                    form.setFieldValue('slug', value);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label={t('common.description')}>
            <TextArea rows={4} placeholder={t('events.descriptionPlaceholder')} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="dates"
                label={t('events.dateTime')}
                rules={[{ required: true, message: t('common.requiredField') }]}
              >
                <RangePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="timezone"
                label={t('events.timezoneLabel')}
                initialValue="Europe/Budapest"
              >
                <Select>
                  <Option value="Europe/Budapest">{t('events.tzBudapest')}</Option>
                  <Option value="UTC">{t('events.tzUtc')}</Option>
                  <Option value="America/New_York">{t('events.tzNewYork')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="language"
                label={t('events.languageLabel')}
                initialValue="HU"
                tooltip={t('events.languageTooltip')}
              >
                <Select>
                  <Option value="HU">{t('events.languageHu')}</Option>
                  <Option value="EN">{t('events.languageEn')}</Option>
                  <Option value="SR">{t('events.languageSr')}</Option>
                  <Option value="NL">{t('events.languageNl')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="location" label={t('events.location')}>
                <Input placeholder={t('events.locationPlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="venue" label={t('events.venueLabel')}>
                <Input placeholder={t('events.venuePlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="mapLatitude"
                label={t('events.latLabel')}
                extra={t('events.latExtra')}
              >
                <InputNumber
                  step={0.0001}
                  style={{ width: '100%' }}
                  placeholder="46.2054"
                  precision={6}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="mapLongitude"
                label={t('events.lngLabel')}
                extra={t('events.lngExtra')}
              >
                <InputNumber
                  step={0.0001}
                  style={{ width: '100%' }}
                  placeholder="19.8892"
                  precision={6}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="mapZoom"
                label={t('events.zoomLabel')}
                extra={t('events.zoomExtra')}
                initialValue={15}
              >
                <InputNumber min={1} max={20} style={{ width: '100%' }} placeholder="15" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="maxAttendees" label={t('events.maxAttendeesLabel')}>
                <InputNumber
                  min={1}
                  style={{ width: '100%' }}
                  placeholder={t('events.maxAttendeesPlaceholder')}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="isActive" label={t('events.active')} initialValue={true}>
                <Select>
                  <Option value={true}>{t('common.yes')}</Option>
                  <Option value={false}>{t('common.no')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="accessMode"
                label={t('events.accessModeLabel')}
                initialValue="TICKET_REQUIRED"
                tooltip={t('events.accessModeTooltip')}
              >
                <Select>
                  <Option value="OPEN">{t('events.accessOpenOption')}</Option>
                  <Option value="TICKET_REQUIRED">{t('events.accessTicketRequired')}</Option>
                  <Option value="PRIVATE">{t('events.accessPrivateOption')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="pwaEnabled"
                label={t('events.pwaLabel')}
                initialValue={true}
                tooltip={t('events.pwaTooltip')}
              >
                <Select>
                  <Option value={true}>{t('events.pwaYes')}</Option>
                  <Option value={false}>{t('common.no')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="allowedRegistrantTypes"
                label={t('events.registrantTypesLabel')}
                initialValue={['company', 'individual']}
                tooltip={t('events.registrantTypesTooltip')}
              >
                <Select mode="multiple" placeholder={t('events.registrantTypesPlaceholder')}>
                  <Option value="company">{t('events.typeCompany')}</Option>
                  <Option value="individual">{t('events.typeIndividual')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="currency"
                label={t('events.currencyLabel')}
                initialValue="EUR"
                tooltip={t('events.currencyTooltip')}
              >
                <Select
                  // Szerkesztésnél csak akkor szerkeszthető, ha a valódi
                  // értéket be tudtuk tölteni a védett configból (#111).
                  disabled={!!editingEvent && !currencyLoaded}
                  showSearch
                  placeholder={t('events.currencyPlaceholder')}
                  options={[
                    { value: 'EUR', label: t('events.currencyEur') },
                    { value: 'HUF', label: t('events.currencyHuf') },
                    { value: 'RSD', label: t('events.currencyRsd') },
                    { value: 'USD', label: t('events.currencyUsd') },
                    { value: 'GBP', label: t('events.currencyGbp') },
                    { value: 'RON', label: t('events.currencyRon') },
                  ]}
                  filterOption={(input, option) =>
                    String(option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="coverImage" label={t('events.coverImageLabel')}>
            <ImageUpload
              uploadUrl="/api/upload/image"
              fieldName="image"
              placeholder={t('events.coverImagePlaceholder')}
              maxSize={5}
            />
          </Form.Item>

          <Form.Item name="logo" label={t('events.logoLabel')}>
            <ImageUpload
              uploadUrl="/api/upload/logo"
              fieldName="logo"
              placeholder={t('events.logoPlaceholder')}
              maxSize={2}
            />
          </Form.Item>

          <Form.Item
            name="organizationId"
            label={t('events.columnOrganization')}
            rules={[{ required: true, message: t('common.requiredField') }]}
            tooltip={user?.role !== 'SUPER_ADMIN' ? t('events.organizationTooltip') : undefined}
          >
            <Select
              placeholder={t('events.selectOrganization')}
              disabled={user?.role !== 'SUPER_ADMIN'}
              showSearch
              filterOption={(input, option) =>
                String(option?.children ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {organizations.map((org) => (
                <Option key={org.id} value={org.id}>
                  {org.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingEvent ? t('events.save') : t('common.create')}
              </Button>
              <Button onClick={closeModal}>{t('events.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <EventTicketTypes
        event={selectedEvent}
        visible={ticketTypesModalVisible}
        onClose={() => {
          setTicketTypesModalVisible(false);
          setSelectedEvent(null);
        }}
      />

      {selectedEvent && sponsorLevelsModalVisible && (
        <EventSponsorLevels
          event={selectedEvent}
          visible={sponsorLevelsModalVisible}
          onClose={() => {
            setSponsorLevelsModalVisible(false);
            setSelectedEvent(null);
          }}
        />
      )}
    </div>
  );
};

export default Events;
